import { useCallback, useEffect, useReducer, useState } from 'react';
import useFetch from './fetch.hook';

const actions = {
    loading: 'loading',
    success: 'success',
    error: 'error',
    abort: 'abort'
};

const initialState = {
    isInitialLoading: true,
    isLoading: true,
    data: null,
    error: null,
};

function reducer(state, action) {
    switch (action.type) {
        case actions.loading: {
            return {
                ...state,
                isLoading: true,
                data: null,
                error: null,
            }
        };
        case actions.success: {
            return {
                isLoading: false,
                isInitialLoading: false,
                data: action.payload || {},
                error: null,
            }
        };
        case actions.error: {
            return {
                isLoading: false,
                isInitialLoading: false,
                data: null,
                error: action.payload || 'Something went wrong! Please try again later.',
            }
        };
        case actions.abort: {
            return {
                isLoading: false,
                isInitialLoading: false,
                data: null,
                error: null,
            }
        };
    }
};

/**
 * 
 * @param {string | null} url 
 * @param {{
 *   method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
 *   data?: Record<string, any>;
 *   responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer' | 'formData';
 *   additionalHeaders?: Record<string, any>;
 * }} [config] 
 * @param {number} [updateTrigger]
 * @returns {{
 *   isInitialLoading: boolean;
 *   isLoading: boolean;
 *   data: any;
 *   error: string | null;
 *   refresh: () => void;
 * }}
 */
const useFetchData = (
    url,
    config = {},
    updateTrigger,
) => {
    const jsonData = config?.data ? JSON.stringify(config.data) : undefined;
    const [state, dispatch] = useReducer(reducer, initialState);
    const [trigger, setTrigger] = useState(0);

    const _fetch = useFetch();

    const refresh = useCallback(() => setTrigger((prev) => prev + 1), []);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const fetcher = async () => {
            if (!url) return dispatch({ type: actions.abort });

            dispatch({ type: actions.loading });

            const method = config?.method || 'get';
            const responseType = config?.responseType || 'json';

            const { data, error } = await _fetch(method, url, config?.data, {
                responseType,
                additionalHeaders: config?.additionalHeaders,
                signal: controller.signal,
            });

            if (error) return dispatch({ type: actions.error, payload: error });
            return dispatch({ type: actions.success, payload: data });
        }

        isMounted && fetcher();

        return () => {
            isMounted = false;
            controller.abort();
        }
    }, [_fetch, trigger, url, updateTrigger, jsonData]);

    return {
        isInitialLoading: state.isInitialLoading,
        isLoading: state.isLoading,
        data: state.data,
        error: state.error,
        refresh,
    }
};

export default useFetchData;